import {Button, Steps} from 'antd'
import {getId, getType} from 'apis/model/base'
import AppIcon from 'components/AppIcon'
import DecoratedInputField from 'components/form/fields/DecoratedInputField'
import {ObjectSelectField} from 'components/form/fields/ObjectSelectField'
import {CustomToggleField} from 'components/form/fields/ToggleField'
import FieldsFactory from 'components/form/FieldsFactory'
import {createValue} from 'components/form/utils'
import {FormikConsumer} from 'formik'
import getAvatar from 'helpers/getAvatar'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {compose, mapProps} from 'recompose'
import {getAllPackages, getCurrentPackageIndex, getLogin,} from 'redux/selectors'
import useAsyncAction from "../../modules/asyncCache/useAsyncAction";
import {organization_getOrganizationsByAuth_Api} from "../../apis";
import {getResponseItems} from "../Shared";
import {useRouteSearchParam} from "../../envs/_current/history";
import {Section} from "components/form/ModernForm";

const renderOwnerItem = ({id, type, label, avatar, actions}) => {
    return (
        <div className="flex items-center gap-2">
            <AppIcon
                size={30}
                item={{
                    id, avatar,
                    title: label, _type: type,
                }}
            />
            <div className="flex flex-col flex-1">
                <div className="font-bold text-xs text-color-000 leading-snug">
                    {label}
                </div>
                {!!type && (
                    <Translate>
                        {(t) => (
                            <span className="font-medium text-2xs text-blue-600 italic leading-snug">
                                {t(type)}
                            </span>
                        )}
                    </Translate>
                )}
            </div>
            {actions}
        </div>
    )
}

const FirstScreen = () => {
    const t = useTranslate()
    const login = useSelector(getLogin)
    const [organizations, setOrganizations] = useState([])

    const allPackages = getAllPackages(login) || []
    const current = getCurrentPackageIndex(login) || 0
    const currentPackage = _.get(allPackages, current) || {}

    const channels_maximum = _.get(currentPackage, 'channels_maximum')
    const total_channels = _.get(login, 'total_channels')
    const owner_id = useRouteSearchParam("owner_id")

    const {handleAsyncAction: getOrganizationsByAuth} = useAsyncAction({
        apiInfo: organization_getOrganizationsByAuth_Api,
        onSuccess: (__, data) => {
            const items = getResponseItems(data?.response)
            setOrganizations(items)
        },
    })

    useEffect(() => {
        if (owner_id) {
            getOrganizationsByAuth({organization_id: owner_id});
        } else {
            getOrganizationsByAuth();
        }
    }, [])

    // Comment lại đoạn này, chỉ cho tạo kênh trong ORG
    const owner_options = [
        ...organizations.map(e => ({
            id: getId(e),
            type: getType(e),
            value: getId(e),
            label: e.title,
            title: getTitle(e),
            avatar: getAvatar(e),
        })),
    ].map(({label, ...rest}) => ({
        key: rest?.id,
        label: renderOwnerItem({
            label,
            ...rest,
            actions: [
                <span className="font-medium text-xs text-primary hover:text-primary-700 tracking-wide cursor-pointer">
                    {t('choose')}
                </span>,
            ],
        }),
        render_as: renderOwnerItem({
            label,
            ...rest,
        }),
        ...rest,
    }))

    return (
        <>
            {channels_maximum <= total_channels + 3 && (
                <div className="p-3 flex-col rounded border border-dashed verticalList">
                    <div className="text-color-300 tracking-wider italic">
                        {`${t('Number of your channels')}: `}
                        <span className="font-bold text-color-000 ml-1">
                            {total_channels}
                        </span>
                    </div>
                    <div>
                        <Steps
                            current={current}
                            size="small">
                            {allPackages.map((p) => (
                                <Steps.Step
                                    key={p.name}
                                    title={t(p.name)}
                                    description={
                                        p.channels_maximum === 9999
                                            ? t('unlimited')
                                            : t('{n} channels', {
                                                n: p.channels_maximum,
                                            })
                                    }
                                />
                            ))}
                        </Steps>
                        <div className="py-3 mt-3">
                            {channels_maximum === 9999
                                ? t('unlimited')
                                : t(
                                    'you currently have a maximum limit of {n} channels allowed to own. You can change it by upgrading your account',
                                    {
                                        n: channels_maximum,
                                    }
                                )}
                        </div>
                    </div>
                </div>
            )}
            <div className="flex flex-col gap-4">
                <Section title={t('basic information')}>
                    <FieldsFactory
                        formSchema={[
                            {
                                children: {
                                    name: 'title',
                                    component: compose(
                                        withTranslate,
                                        mapProps(({name, value, onChange, translate}) => ({
                                            name,
                                            value,
                                            onChange,
                                            size: 'large',
                                            variant: 'borderless',
                                            label: translate('what your channel name'),
                                            autoFocus: true,
                                            placeholder: translate('enter your channel name'),
                                        }))
                                    )(DecoratedInputField),
                                },
                            },
                            {
                                children: {
                                    name: 'owner',
                                    component: mapProps(({name, value, form = {}, onChange}) => {
                                        const defaultValue = _.get(form.values, 'default_owner_id')
                                        return {
                                            name,
                                            value,
                                            onChange,
                                            variant: 'borderless',
                                            size: 'large',
                                            label: 'owner',
                                            defaultValue: _.find(owner_options, {value: defaultValue,}),
                                            showSearch: false,
                                            options: owner_options,
                                            optionLabelProp: 'render_as',
                                            optionFilterProp: 'title',
                                        }
                                    })(ObjectSelectField),
                                },
                            },
                        ]}
                    />
                </Section>
                <Section title={t('privacy')}>
                    <FieldsFactory
                        formSchema={[
                            {
                                children: {
                                    title: 'private channel',
                                    name: 'privacy',
                                    component: compose(
                                        withTranslate,
                                        mapProps(({name, value, onChange, translate}) => ({
                                            name,
                                            value: Number(value) === 2,
                                            onChange: (value) => {
                                                onChange(createValue(name, Boolean(value) ? 2 : 1))
                                            },
                                            title: 'private channel',
                                            description: 'by making a channel private, only members will have access to this channel',
                                            className: "p-2 border border-dashed border-secondary rounded-lg"
                                        }))
                                    )(CustomToggleField),
                                },
                            },
                        ]}
                    />
                </Section>
            </div>
        </>
    )
}
const createChannelFormRoutes = [
    {
        path: '/first-screen',
        name: 'create channel',
        component: FirstScreen,
        exact: true,
    },
]
export default function CreateChannelForm({
                                              asyncData: {isLoading} = {},
                                              handleSubmit,
                                              routes = createChannelFormRoutes,
                                          }) {
    const t = useTranslate()
    const [currentIndex, setCurrentIndex] = useState(0)

    const handleOnOk = () => {
        setCurrentIndex(Math.min(routes.length - 1, currentIndex + 1))
        if (currentIndex === routes.length - 1) {
            handleSubmit()
        }
    }

    const handleOnCancel = () => setCurrentIndex(Math.max(0, currentIndex - 1))

    const C = createChannelFormRoutes[currentIndex].component

    return (
        <div className="flex flex-col flex-1 gap-6">
            <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                {t(createChannelFormRoutes[currentIndex].name)}
            </div>
            <div className="flex flex-col gap-4">
                <C
                    {...{
                        onNext: handleOnOk,
                        onBack: handleOnCancel,
                    }}
                />
            </div>
            <div className="sticky bottom-0 flex justify-center">
                <FormikConsumer>
                    {({errors}) => (
                        <Button
                            disabled={!_.isEmpty(errors)}
                            loading={isLoading}
                            className="button-rounded-lg no-border"
                            color="primary" variant="solid"
                            onClick={handleOnOk}>
                            {t('create')}
                        </Button>
                    )}
                </FormikConsumer>
            </div>
        </div>
    )
}
